import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "services/api";
import { __ } from "services/Translator";

export default function CountrySelect({
  selected = null,
  onChange,
  placeholder = __("Search country or code..."),
  className = "country-select",
  listMaxHeight = 320,
  variant = "country",
  position = "below", // kept for API compat; the panel now auto-flips to fit
  allowedCodes = null,
  resolveStringSelection = true,
  buttonClassName = "",
  panelClassName = "",
  searchInputClassName = "",
  optionClassName = "",
  showSearch = true,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Fixed-position coordinates for the portalled panel (so it can never be
  // clipped by an ancestor modal with overflow:hidden — the bug where the
  // country list was cut off mid-alphabet inside the register modal).
  const [coords, setCoords] = useState(null);

  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchCountries = async () => {
    try {
      setBusy(true);
      // Backend filters out BO-blocked countries by default (self-heal).
      const res = await api.get("/countries");
      if (res.status === 200) setCountries(res.data || []);
    } catch (_) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const selectedResolved = useMemo(() => {
    if (!selected) return null;
    if (typeof selected === "string") {
      const code = selected.toUpperCase();
      return countries.find((c) => (c.code || "").toUpperCase() === code) || null;
    }
    return selected;
  }, [selected, countries]);

  const allowedCodeSet = useMemo(() => {
    if (!Array.isArray(allowedCodes) || !allowedCodes.length) {
      return null;
    }
    return new Set(allowedCodes.map((code) => String(code || "").toUpperCase()).filter(Boolean));
  }, [allowedCodes]);

  const availableCountries = useMemo(() => {
    const list = allowedCodeSet
      ? countries.filter((c) => allowedCodeSet.has((c.code || "").toUpperCase()))
      : countries.slice();
    // Alphabetical by display name so the list is scannable (the API returns
    // them in insertion order, which reads as random when scrolling).
    return list.sort((a, b) =>
      String(a.nicename || a.name || a.code || "").localeCompare(String(b.nicename || b.name || b.code || ""))
    );
  }, [countries, allowedCodeSet]);

  useEffect(() => {
    if (!resolveStringSelection) return;
    if (!selected || typeof selected !== "string") return;
    if (!countries.length) return;

    const code = selected.toUpperCase();
    const found = availableCountries.find((c) => (c.code || "").toUpperCase() === code);
    if (found) {
      onChange?.(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, availableCountries, resolveStringSelection]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableCountries;
    return availableCountries.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const nicename = (c.nicename || "").toLowerCase();
      const iso = (c.code || "").toLowerCase();
      const phonecode = String(c.phonecode || "").toLowerCase();
      return (
        name.includes(q) || nicename.includes(q) || iso.includes(q) || phonecode.includes(q) || ("+" + phonecode).includes(q)
      );
    });
  }, [availableCountries, query]);

  useEffect(() => {
    if (highlight > filtered.length - 1) {
      setHighlight(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, highlight]);

  // Measure the trigger and pick the roomier side (below vs above), sizing the
  // list to the space actually available in the viewport.
  const computePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const gap = 6;
    const edge = 10; // keep clear of the viewport edges
    const searchH = showSearch ? 54 : 0;
    const spaceBelow = vh - r.bottom - gap - edge;
    const spaceAbove = r.top - gap - edge;
    // Open upward only when below is too tight AND above genuinely has more room.
    const openUp = spaceBelow < Math.min(listMaxHeight + searchH, 240) && spaceAbove > spaceBelow;
    const avail = Math.max(160, openUp ? spaceAbove : spaceBelow);
    const listH = Math.max(140, Math.min(listMaxHeight, avail - searchH));
    setCoords({
      left: Math.round(r.left),
      width: Math.round(r.width),
      openUp,
      top: openUp ? null : Math.round(r.bottom + gap),
      bottom: openUp ? Math.round(vh - r.top + gap) : null,
      listMaxHeight: Math.round(listH),
    });
  };

  // Recompute on open and keep it anchored while the page scrolls/resizes.
  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    computePosition();
    const onWin = () => computePosition();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true); // capture: also catch ancestor scroll
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    function onDocClick(e) {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && showSearch) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, showSearch]);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.querySelector(`[data-index="${highlight}"]`);
    if (!item) return;
    const { offsetTop, offsetHeight } = item;
    const { scrollTop, clientHeight } = listRef.current;
    if (offsetTop < scrollTop) listRef.current.scrollTop = offsetTop;
    else if (offsetTop + offsetHeight > scrollTop + clientHeight) {
      listRef.current.scrollTop = offsetTop - (clientHeight - offsetHeight);
    }
  }, [highlight]);

  const selectItem = (idx) => {
    const item = filtered[idx];
    if (!item) return;
    onChange?.(item);
    setOpen(false);
    setQuery("");
  };

  const onButtonKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectItem(highlight);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  const selectedLabel =
    variant === "country"
      ? selectedResolved
        ? `${selectedResolved.nicename || selectedResolved.code}`
        : __("Select country")
      : selectedResolved
        ? `+${selectedResolved.phonecode} (${selectedResolved.code})`
        : __("Select country");

  const getFlagSrc = (iso2) => `/flags/${iso2.toLowerCase()}.svg`;

  const panelStyle = coords
    ? {
        position: "fixed",
        zIndex: 100000,
        left: coords.left,
        width: coords.width,
        ...(coords.openUp ? { bottom: coords.bottom } : { top: coords.top }),
        background: "#14261c",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 10,
        boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
        overflow: "hidden",
      }
    : { display: "none" };

  const panel = (
    <div ref={panelRef} className={`country-select__panel ${panelClassName}`.trim()} role="listbox" style={panelStyle}>
      {showSearch && (
        <div style={{ padding: 8 }}>
          <input
            ref={inputRef}
            type="text"
            className={`country-select__search ${searchInputClassName}`.trim()}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={placeholder}
          />
        </div>
      )}

      <div ref={listRef} style={{ maxHeight: coords ? coords.listMaxHeight : listMaxHeight, overflowY: "auto" }}>
        {filtered.length === 0 && <div style={{ padding: 12, color: "#bbb" }}>{__("No matches")}</div>}

        {filtered.map((c, idx) => {
          const isActive = idx === highlight;
          return (
            <div
              key={c.code || c.id || idx}
              data-index={idx}
              role="option"
              aria-selected={selectedResolved?.code === c.code}
              className={`country-select__option ${isActive ? "is-active" : ""} ${optionClassName}`.trim()}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(idx)}
            >
              <img
                src={getFlagSrc(c.code)}
                alt={c.nicename || c.code}
                style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2, flex: "0 0 auto" }}
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
              />
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", minWidth: 0 }}>
                <span style={{ color: "#ccc" }}>{c.code}</span>
                {variant === "country" && (c.nicename || c.name) && (
                  <span style={{ color: "#aaa", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    • {c.nicename || c.name}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={className} ref={rootRef} style={{ position: "relative", minWidth: 50 }}>
      <button
        ref={buttonRef}
        type="button"
        className={`country-select__button ${buttonClassName}`.trim()}
        onClick={() => !busy && setOpen((o) => !o)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        {selectedResolved?.code ? (
          <img
            src={getFlagSrc(selectedResolved.code)}
            alt={selectedResolved.nicename || selectedResolved.code}
            style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2 }}
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : (
          <div style={{ width: 22, height: 16, background: "#555", borderRadius: 2 }} />
        )}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedLabel}</span>
        <span style={{ marginLeft: "auto", opacity: 0.7, flex: "0 0 auto" }}>▾</span>
      </button>

      {open && coords && createPortal(panel, document.body)}
    </div>
  );
}
